import {
  apiErrorResponseSchema,
  emailLoginCodeChallengeSchema,
  emailLoginCompletionSchema,
  accountAliasSchema,
  authenticationStatusSchema,
  type AccountAlias,
  developmentLoginCodeListSchema,
  type DevelopmentLoginCode,
  authenticatedUserSchema,
  chatMessageSchema,
  conversationListSchema,
  conversationPreferenceSchema,
  type ConversationPreference,
  groupViewSchema,
  type GroupView,
  identityVerificationChallengeSchema,
  messageHistoryPageSchema,
  notificationPageSchema,
  type NotificationPage,
  messageLocationSchema,
  adminAccountListSchema,
  adminConversationListSchema,
  adminMessageListSchema,
  adminNotificationListSchema,
  operatorAccountSchema,
  operatorSmsDeliveryListSchema,
  phoneLoginCompletionSchema,
  signedInDeviceListSchema,
  threadViewSchema,
  type AttachmentReferenceInput,
  type AuthenticationStatus,
  type AuthenticatedUser,
  type ChatMessage,
  type ColorScheme,
  type ConversationSummary,
  type IdentityVerificationChallenge,
  type MessageHistoryPage,
  type MessageLocation,
  type AdminAccountList,
  type AdminConversation,
  type AdminMessageEntry,
  type AdminNotification,
  type OperatorAccount,
  type OperatorSmsDelivery,
  type PhoneLoginCompletion,
  type SignedInDevice,
  type SupportedLocale,
  type UpdateProfileRequest,
  type ThreadView,
  type UploadedAttachment,
  uploadAttachmentResponseSchema,
} from '@threadline/shared'
import { z, type ZodType } from 'zod'
import { buildAuthorizationHeaders } from './activeAccount.ts'

export class BackendApiError extends Error {
  constructor(
    readonly httpStatusCode: number,
    readonly errorCode: string,
    readonly localizationKey: string,
  ) {
    super(`${errorCode} (${httpStatusCode})`)
    this.name = 'BackendApiError'
  }
}

const UNEXPECTED_ERROR_LOCALIZATION_KEY = 'common.somethingWentWrong'

/**
 * Listeners notified when the backend rejects this tab's session token, so the application
 * can send the person back to the sign-in screen with their account preselected instead of
 * leaving them looking at a screen that quietly fails to load.
 */
const rejectedSessionListeners = new Set<() => void>()

export function onSessionRejected(handleRejectedSession: () => void): () => void {
  rejectedSessionListeners.add(handleRejectedSession)
  return () => rejectedSessionListeners.delete(handleRejectedSession)
}

function announceRejectedSession(): void {
  for (const handleRejectedSession of [...rejectedSessionListeners]) {
    handleRejectedSession()
  }
}

async function requestBackend<TResponse>(
  requestPath: string,
  requestOptions: {
    method?: string
    body?: unknown
    responseSchema?: ZodType<TResponse>
  } = {},
): Promise<TResponse> {
  const httpResponse = await fetch(`/api${requestPath}`, {
    method: requestOptions.method ?? 'GET',
    headers: {
      ...buildAuthorizationHeaders(),
      ...(requestOptions.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: requestOptions.body === undefined ? undefined : JSON.stringify(requestOptions.body),
  })

  if (!httpResponse.ok) {
    if (httpResponse.status === 401) {
      announceRejectedSession()
    }
    const parsedError = apiErrorResponseSchema.safeParse(await readJsonSafely(httpResponse))
    throw new BackendApiError(
      httpResponse.status,
      parsedError.success ? parsedError.data.errorCode : 'unknown-error',
      parsedError.success ? parsedError.data.localizationKey : UNEXPECTED_ERROR_LOCALIZATION_KEY,
    )
  }

  if (requestOptions.responseSchema === undefined) {
    return undefined as TResponse
  }
  return requestOptions.responseSchema.parse(await httpResponse.json())
}

async function readJsonSafely(httpResponse: Response): Promise<unknown> {
  try {
    return await httpResponse.json()
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Authentication and account
// ---------------------------------------------------------------------------

export function fetchAuthenticationStatus(): Promise<AuthenticationStatus> {
  return requestBackend('/auth/status', {
    responseSchema: authenticationStatusSchema,
  })
}

export function requestEmailLoginCode(emailAddress: string, redirectPath: string) {
  return requestBackend('/auth/email-code/requests', {
    method: 'POST',
    body: { emailAddress, redirectPath },
    responseSchema: emailLoginCodeChallengeSchema,
  })
}

export function verifyEmailLoginCode(challengeId: string, loginCode: string) {
  return requestBackend('/auth/email-code/verify', {
    method: 'POST',
    body: { challengeId, loginCode },
    responseSchema: emailLoginCompletionSchema,
  })
}

/**
 * The codes this deployment just sent, for the development panel. The route only exists
 * where the deployment turned the tools on, so a 404 here is the ordinary answer and not
 * something to report.
 */
export function fetchRecentLoginCodes(): Promise<{
  loginCodes: DevelopmentLoginCode[]
}> {
  return requestBackend('/development/login-codes', {
    responseSchema: developmentLoginCodeListSchema,
  })
}

/**
 * Empties what the panel shows. The codes are held by the deployment rather than by the
 * browser, so the reset that forgets everything this origin kept has to ask for them.
 */
export function forgetRecentLoginCodes(): Promise<void> {
  return requestBackend('/development/login-codes', { method: 'DELETE' })
}

/**
 * What became of this deployment's text messages, for the operator panel. Unlike the
 * development routes this one always exists and always requires a session: a 401 means
 * nobody is signed in here, a 403 means somebody without the role asked.
 */
export function fetchOperatorSmsDeliveries(query?: {
  handle?: string
  limit?: number
}): Promise<{ deliveries: OperatorSmsDelivery[] }> {
  const searchParameters = new URLSearchParams()
  if (query?.handle !== undefined && query.handle.trim().length > 0) {
    searchParameters.set('handle', query.handle.trim())
  }
  if (query?.limit !== undefined) {
    searchParameters.set('limit', String(query.limit))
  }
  const queryString = searchParameters.toString()
  return requestBackend(
    `/operator/sms-deliveries${queryString.length > 0 ? `?${queryString}` : ''}`,
    {
      responseSchema: operatorSmsDeliveryListSchema,
    },
  )
}

/**
 * The account behind a handle, with whether it is banned. Read-only: looking somebody
 * up never brings an account into existence, and an unknown handle answers 404.
 */
export function fetchOperatorAccount(handle: string): Promise<OperatorAccount> {
  return requestBackend(`/operator/accounts?handle=${encodeURIComponent(handle)}`, {
    responseSchema: operatorAccountSchema,
  })
}

/**
 * Stops an account from using the installation right now: sessions revoked, streams
 * closed, sign-in refused. Silent by design — no email is ever sent about a ban.
 */
export function banOperatorAccount(handle: string): Promise<OperatorAccount> {
  return requestBackend('/operator/account-ban', {
    method: 'POST',
    body: { handle },
    responseSchema: operatorAccountSchema,
  })
}

/** Lets a banned account back in. Revoked sessions stay revoked. */
export function liftOperatorAccountBan(handle: string): Promise<OperatorAccount> {
  return requestBackend('/operator/account-ban-lift', {
    method: 'POST',
    body: { handle },
    responseSchema: operatorAccountSchema,
  })
}

function buildAdminPageQuery(query: {
  handle?: string
  search?: string
  channel?: string
  delivery?: string
  cursor?: string | null
  limit?: number
}): string {
  const searchParameters = new URLSearchParams()
  if (query.handle !== undefined && query.handle.trim().length > 0) {
    searchParameters.set('handle', query.handle.trim())
  }
  if (query.search !== undefined && query.search.trim().length > 0) {
    searchParameters.set('search', query.search.trim())
  }
  if (query.channel !== undefined && query.channel.length > 0) {
    searchParameters.set('channel', query.channel)
  }
  if (query.delivery !== undefined && query.delivery.length > 0) {
    searchParameters.set('delivery', query.delivery)
  }
  if (query.cursor !== undefined && query.cursor !== null) {
    searchParameters.set('cursor', query.cursor)
  }
  if (query.limit !== undefined) {
    searchParameters.set('limit', String(query.limit))
  }
  const queryString = searchParameters.toString()
  return queryString.length > 0 ? `?${queryString}` : ''
}

/** Every account, newest first. Operator eyes only, like every route below. */
export function fetchAdminAccounts(query?: {
  search?: string
  cursor?: string | null
}): Promise<AdminAccountList> {
  return requestBackend(`/operator/accounts${buildAdminPageQuery(query ?? {})}`, {
    responseSchema: adminAccountListSchema,
  })
}

/** The most recent messages of the whole installation, whatever conversation. */
export function fetchAdminRecentMessages(query?: {
  cursor?: string | null
}): Promise<{ messages: AdminMessageEntry[]; nextCursor: string | null }> {
  return requestBackend(`/operator/messages/recent${buildAdminPageQuery(query ?? {})}`, {
    responseSchema: adminMessageListSchema,
  })
}

/** One person's conversations: pairs and groups, by latest message. */
export function fetchAdminConversations(
  handle: string,
): Promise<{ conversations: AdminConversation[] }> {
  return requestBackend(`/operator/conversations${buildAdminPageQuery({ handle })}`, {
    responseSchema: adminConversationListSchema,
  })
}

/** One conversation timeline, read-only: nothing here can be answered. */
export function fetchAdminConversationMessages(query: {
  accountId: string
  counterpartId: string
  cursor?: string | null
}): Promise<{ messages: AdminMessageEntry[]; nextCursor: string | null }> {
  const searchParameters = new URLSearchParams({
    accountId: query.accountId,
    counterpartId: query.counterpartId,
  })
  if (query.cursor !== undefined && query.cursor !== null) {
    searchParameters.set('cursor', query.cursor)
  }
  return requestBackend(`/operator/conversation-messages?${searchParameters.toString()}`, {
    responseSchema: adminMessageListSchema,
  })
}

/** Notification bursts with what each channel managed. */
export function fetchAdminNotifications(query?: {
  handle?: string
  channel?: string
  delivery?: string
  cursor?: string | null
}): Promise<{ notifications: AdminNotification[]; nextCursor: string | null }> {
  return requestBackend(`/operator/notifications${buildAdminPageQuery(query ?? {})}`, {
    responseSchema: adminNotificationListSchema,
  })
}

/** Renaming somebody in your own address book. The viewer is always the caller. */
/**
 * What this person missed, newest first. `before` continues an older page, so the screen
 * never asks for what it already shows.
 */
export function fetchNotificationPage(page?: {
  beforeNotificationId?: number
}): Promise<NotificationPage> {
  const query =
    page?.beforeNotificationId === undefined ? '' : `?before=${page.beforeNotificationId}`
  return requestBackend(`/notifications${query}`, {
    responseSchema: notificationPageSchema,
  })
}

export function markEveryNotificationRead(): Promise<void> {
  return requestBackend('/notifications/read-all', {
    method: 'POST',
    body: {},
  })
}

export function setAccountAlias(subjectAccountId: string, alias: string): Promise<AccountAlias> {
  return requestBackend(`/accounts/${encodeURIComponent(subjectAccountId)}/alias`, {
    method: 'PUT',
    body: { alias },
    responseSchema: accountAliasSchema,
  })
}

export function requestPasswordlessLoginLink(loginLinkRequest: {
  emailAddress: string
  redirectPath?: string
}): Promise<void> {
  return requestBackend('/auth/magic-link/requests', {
    method: 'POST',
    body: loginLinkRequest,
  })
}

/** Texts a four digit code to a phone number, which is how a phone signs in. */
export function requestPhoneLoginCode(phoneNumber: string): Promise<void> {
  return requestBackend('/auth/sms/requests', {
    method: 'POST',
    body: { phoneNumber },
  })
}

export function verifyPhoneLoginCode(verification: {
  phoneNumber: string
  loginCode: string
}): Promise<PhoneLoginCompletion> {
  return requestBackend('/auth/sms/verifications', {
    method: 'POST',
    body: verification,
    responseSchema: phoneLoginCompletionSchema,
  })
}

/**
 * Adds another way of reaching or proving the account in use. The server answers with how
 * it is being proved: four digits to retype, or a link that was mailed.
 */
export function requestIdentityAttachment(handle: string): Promise<IdentityVerificationChallenge> {
  return requestBackend('/account/identities', {
    method: 'POST',
    body: { handle },
    responseSchema: identityVerificationChallengeSchema,
  })
}

/** Completes an attachment with the four digits that were texted. */
export function confirmAttachedIdentity(confirmation: {
  handle: string
  loginCode: string
}): Promise<AuthenticatedUser> {
  return requestBackend('/account/identities/verifications', {
    method: 'POST',
    body: confirmation,
    responseSchema: authenticatedUserSchema,
  })
}

/**
 * Removes this account from this device for good. Leaving an account is not this: that
 * happens entirely in the browser and keeps the token, so the account can be picked again
 * with one click.
 */
export function revokeCurrentSession(): Promise<void> {
  return requestBackend('/auth/sessions/current', { method: 'DELETE' })
}

/** Signs the account out of every device it was ever signed in on. */
export async function revokeAllSessions(): Promise<number> {
  const revocation = await requestBackend('/auth/sessions', {
    method: 'DELETE',
    responseSchema: z.object({
      revokedSessionCount: z.number().int().nonnegative(),
    }),
  })
  return revocation.revokedSessionCount
}

export async function fetchSignedInDevices(): Promise<SignedInDevice[]> {
  const deviceList = await requestBackend('/auth/sessions', {
    responseSchema: signedInDeviceListSchema,
  })
  return deviceList.devices
}

/**
 * Muting and archiving one conversation. Each switch travels on its own, so changing one
 * never quietly changes the other, and omitting one leaves it exactly as it was.
 */
export function updateConversationPreference(
  counterpartHandle: string,
  preference: { muted?: boolean; archived?: boolean },
): Promise<ConversationPreference> {
  return requestBackend(`/conversations/${encodeURIComponent(counterpartHandle)}/preference`, {
    method: 'PUT',
    body: preference,
    responseSchema: conversationPreferenceSchema,
  })
}

/** Light or dark, or `null` to follow the device again. Stored on the account. */
export function updatePreferredColorScheme(
  preferredColorScheme: ColorScheme | null,
): Promise<AuthenticatedUser> {
  return requestBackend('/account/preferred-color-scheme', {
    method: 'PUT',
    body: { preferredColorScheme },
    responseSchema: authenticatedUserSchema,
  })
}

export function updatePreferredLocale(
  preferredLocale: SupportedLocale,
): Promise<AuthenticatedUser> {
  return requestBackend('/account/preferred-locale', {
    method: 'PATCH',
    body: { preferredLocale },
    responseSchema: authenticatedUserSchema,
  })
}

// ---------------------------------------------------------------------------
// Web Push
// ---------------------------------------------------------------------------

export async function fetchPushSubscriptions(): Promise<string[]> {
  const subscriptionList = await requestBackend('/push/subscriptions', {
    responseSchema: z.object({ subscribedEndpoints: z.array(z.string()) }),
  })
  return subscriptionList.subscribedEndpoints
}

export function savePushSubscription(pushSubscription: unknown): Promise<void> {
  return requestBackend('/push/subscriptions', {
    method: 'POST',
    body: pushSubscription,
  })
}

export function removePushSubscription(endpoint: string): Promise<void> {
  return requestBackend('/push/subscriptions', {
    method: 'DELETE',
    body: { endpoint },
  })
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

export type SendMessageContent = {
  body?: string
  attachments?: AttachmentReferenceInput[]
}

/**
 * Saves the parts of a profile a person owns: the name they chose, and the picture they
 * uploaded. Only what is passed is changed, so saving a name never removes a picture.
 */
export function updateOwnProfile(profileUpdate: UpdateProfileRequest): Promise<AuthenticatedUser> {
  return requestBackend('/account/profile', {
    method: 'PUT',
    body: profileUpdate,
    responseSchema: authenticatedUserSchema,
  })
}

/**
 * Uploads files through a streaming multipart request. The server computes the SHA-256
 * itself, deduplicates the content and answers with single use upload receipts.
 */
export async function uploadAttachmentFiles(
  filesToUpload: readonly File[],
): Promise<UploadedAttachment[]> {
  const uploadFormData = new FormData()
  for (const fileToUpload of filesToUpload) {
    uploadFormData.append('attachment', fileToUpload, fileToUpload.name)
  }

  const httpResponse = await fetch('/api/attachments/uploads', {
    method: 'POST',
    headers: buildAuthorizationHeaders(),
    body: uploadFormData,
  })

  if (!httpResponse.ok) {
    const parsedError = apiErrorResponseSchema.safeParse(await readJsonSafely(httpResponse))
    throw new BackendApiError(
      httpResponse.status,
      parsedError.success ? parsedError.data.errorCode : 'unknown-error',
      parsedError.success ? parsedError.data.localizationKey : 'attachment.uploadFailed',
    )
  }

  return uploadAttachmentResponseSchema.parse(await httpResponse.json()).uploadedAttachments
}

export function buildAttachmentReference(
  uploadedAttachment: UploadedAttachment,
): AttachmentReferenceInput {
  return {
    uploadReceiptToken: uploadedAttachment.uploadReceiptToken,
    originalFilename: uploadedAttachment.originalFilename,
    contentType: uploadedAttachment.contentType,
  }
}

// ---------------------------------------------------------------------------
// Conversations, messages and threads
// ---------------------------------------------------------------------------

export async function fetchConversations(): Promise<ConversationSummary[]> {
  const conversationList = await requestBackend('/conversations', {
    responseSchema: conversationListSchema,
  })
  return conversationList.conversations
}

export function fetchMessageHistoryPage(
  counterpartHandle: string,
  historyQuery: { beforeMessageId?: number; limit?: number } = {},
): Promise<MessageHistoryPage> {
  const querySearchParameters = new URLSearchParams()
  if (historyQuery.beforeMessageId !== undefined) {
    querySearchParameters.set('beforeMessageId', String(historyQuery.beforeMessageId))
  }
  if (historyQuery.limit !== undefined) {
    querySearchParameters.set('limit', String(historyQuery.limit))
  }
  const querySuffix = querySearchParameters.size > 0 ? `?${querySearchParameters.toString()}` : ''

  return requestBackend(
    `/conversations/${encodeURIComponent(counterpartHandle)}/messages${querySuffix}`,
    { responseSchema: messageHistoryPageSchema },
  )
}

/**
 * Groups.
 *
 * A group is addressed by its id, which has the shape of an account id because the two share
 * one keyspace — so these paths look like the one-to-one ones and are told apart only by
 * living under `/groups`. That is deliberate: it is what let the router and the deep links
 * stay as they were.
 */
export function createGroup(groupContent: {
  name: string
  memberHandles?: string[]
}): Promise<GroupView> {
  return requestBackend('/groups', {
    method: 'POST',
    body: groupContent,
    responseSchema: groupViewSchema,
  })
}

export function fetchGroup(groupId: string): Promise<GroupView> {
  return requestBackend(`/groups/${encodeURIComponent(groupId)}`, {
    responseSchema: groupViewSchema,
  })
}

/** Any member may rename it: the name belongs to the group, not to whoever is looking. */
export function renameGroup(groupId: string, name: string): Promise<GroupView> {
  return requestBackend(`/groups/${encodeURIComponent(groupId)}/name`, {
    method: 'PUT',
    body: { name },
    responseSchema: groupViewSchema,
  })
}

/** Anybody may add anybody, and adding somebody with no account brings one into existence. */
export function addGroupMember(groupId: string, handle: string): Promise<GroupView> {
  return requestBackend(`/groups/${encodeURIComponent(groupId)}/members`, {
    method: 'POST',
    body: { handle },
    responseSchema: groupViewSchema,
  })
}

/** Leaving, and only ever yourself — the server reads the account from the session. */
export function leaveGroup(groupId: string): Promise<void> {
  return requestBackend(`/groups/${encodeURIComponent(groupId)}/members/me`, {
    method: 'DELETE',
  })
}

export function fetchGroupMessageHistoryPage(
  groupId: string,
  historyQuery: { beforeMessageId?: number; limit?: number } = {},
): Promise<MessageHistoryPage> {
  const querySearchParameters = new URLSearchParams()
  if (historyQuery.beforeMessageId !== undefined) {
    querySearchParameters.set('beforeMessageId', String(historyQuery.beforeMessageId))
  }
  if (historyQuery.limit !== undefined) {
    querySearchParameters.set('limit', String(historyQuery.limit))
  }
  const querySuffix = querySearchParameters.size > 0 ? `?${querySearchParameters.toString()}` : ''

  return requestBackend(`/groups/${encodeURIComponent(groupId)}/messages${querySuffix}`, {
    responseSchema: messageHistoryPageSchema,
  })
}

export function sendGroupMessage(
  groupId: string,
  messageContent: SendMessageContent,
): Promise<ChatMessage> {
  return requestBackend(`/groups/${encodeURIComponent(groupId)}/messages`, {
    method: 'POST',
    body: messageContent,
    responseSchema: chatMessageSchema,
  })
}

/**
 * How far this member has read. The mark only moves forward, so reporting an older position
 * from a second device cannot make read messages unread again.
 */
export function markGroupMessagesAsRead(
  groupId: string,
  lastReadMessageId: number,
): Promise<{ unreadMessageCount: number }> {
  return requestBackend(`/groups/${encodeURIComponent(groupId)}/messages/read`, {
    method: 'POST',
    body: { lastReadMessageId },
    responseSchema: z.object({
      unreadMessageCount: z.number().int().nonnegative(),
    }),
  })
}

export function sendTopLevelMessage(
  counterpartHandle: string,
  messageContent: SendMessageContent,
): Promise<ChatMessage> {
  return requestBackend(`/conversations/${encodeURIComponent(counterpartHandle)}/messages`, {
    method: 'POST',
    body: messageContent,
    responseSchema: chatMessageSchema,
  })
}

export function markConversationMessagesAsRead(
  counterpartHandle: string,
  messageIds: number[],
): Promise<void> {
  return requestBackend(`/conversations/${encodeURIComponent(counterpartHandle)}/messages/read`, {
    method: 'POST',
    body: { messageIds },
  })
}

export function fetchThreadView(rootMessageId: number): Promise<ThreadView> {
  return requestBackend(`/messages/${rootMessageId}/thread`, {
    responseSchema: threadViewSchema,
  })
}

export function sendThreadReply(
  rootMessageId: number,
  messageContent: SendMessageContent,
): Promise<ChatMessage> {
  return requestBackend(`/messages/${rootMessageId}/thread/replies`, {
    method: 'POST',
    body: messageContent,
    responseSchema: chatMessageSchema,
  })
}

export function markThreadRepliesAsRead(rootMessageId: number): Promise<void> {
  return requestBackend(`/messages/${rootMessageId}/thread/read`, {
    method: 'POST',
  })
}

export function fetchMessageLocation(messageId: number): Promise<MessageLocation> {
  return requestBackend(`/messages/${messageId}/location`, {
    responseSchema: messageLocationSchema,
  })
}
